import React, {useMemo, useCallback} from 'react';
import PropTypes from 'prop-types';
import JsonFormsWrapper from '@/components/common/jsonForms/jsonFormsWrapper/JsonFormsWrapper';
import {SectionTitle} from '@/components/molecules';
import {PageContainer} from '@/components/organisms';
import {
  activitySelectionUiSchema,
  availabilityJsonSchema,
  availabilityUiSchema,
  pointsAllocationJsonSchema,
  pointsAllocationJsonSchemaForEG,
  pointsAllocationUiSchema,
  pointsAllocationUiSchemaForEG,
  welcomeActivityAvailabilityJsonSchema,
  welcomeActivityAvailabilityUiSchema,
  welcomeActivityEndDateJsonSchema,
  welcomeActivityEndDateUiSchema,
  welcomeActivityHasEndDateSchema,
  welcomeActivityHasEndUiSchema,
  allAvailabilityUiSchema
} from '@/services/schema/activity';
import LoginActivitySetUp from '../LoginActivitySetUp';
import PointsOnOrderDeliverySetUp from '../PointsOnOrderDeliverySetUp';
import WelcomeActivitySetUp from '../WelcomeActivitySetUp';
import PointExpirationSetUp from '../PointExpirationSetUp';
import PayByPointsSetUp from '../PayByPointsSetUp';
import SurveyActivitySetUp from '../SurveyActivitySetUp/SurveyActivitySetUp';
import VideoActivitySetUp from '../VideoActivitySetUp/VideoActivitySetUp';
import FreeKickGameSetUp from '../FreeKickGameSetUp/FreeKickGameSetUp';
import BackgammonGameSetUp from '../BackgammonGameSetUp/BackgammonGameSetUp';
import WheelOfFortuneSetup from '../WheelOfFortuneSetup/WheelOfFortuneSetup';
import EasyOrderActivitySetUp from '../EasyOrderActivitySetUp/EasyOrderActivitySetUp';
import CurrencyToPointsSetUp from '../CurrencyToPointsSetUp';
import useActivitySetup from '@/containers/Activity/ActivitySetUp/ActivitySetup-hook';
import {
  ACTIVITY_TYPE,
  isVideoActivityType,
  isFreeKickActivityType,
  isWheelOfFortuneActivityType,
  isBackgammonActivityType
} from '@/types/Activity';
import {TableSkeletonLoader} from '@/components/atoms';
import {COUNTRY_CODES_FREQUENCY} from '@/utils/constants/globalConstants';
import {useAppSelector} from '@/store';
import DuplicateAlert from '@/components/atoms/Toast/DuplicateAlert';
import {SegmentsCard} from '@/containers/Rewards/RewardSetup/ui-components';
import {SEGMENT_CHIP_TYPE} from '@/utils/constants/commonConstants';
// Helper: Activity Selection
export const ActivitySelection = ({
  activityDropdownTypes,
  formData,
  handleResetFormData,
  handleFormChange
}) => {
  const activityTypeData = useMemo(
    () => ({activityType: formData?.activityType}),
    [formData?.activityType]
  );

  const handleActivityTypeChange = useCallback(
    (data) => {
      if (data?.activityType && data.activityType !== formData?.activityType) {
        handleResetFormData(data);
      } else if (!data?.activityType && formData?.activityType) {
        handleFormChange({activityType: data?.activityType});
      }
    },
    [formData?.activityType, handleResetFormData, handleFormChange]
  );

  return activityDropdownTypes &&
    Object.keys(activityDropdownTypes)?.length > 0 ? (
    <JsonFormsWrapper
      key={formData?.activityType || 'empty'}
      jsonschema={activityDropdownTypes}
      uischema={activitySelectionUiSchema(formData)}
      data={activityTypeData}
      onChange={handleActivityTypeChange}
    />
  ) : null;
};

ActivitySelection.propTypes = {
  activityDropdownTypes: PropTypes.object,
  formData: PropTypes.object,
  handleResetFormData: PropTypes.func.isRequired,
  handleFormChange: PropTypes.func.isRequired
};

// Helper: Activity Type Component
export const ActivityTypeComponent = ({
  activityType,
  formData,
  handleFormChange
}) => {
  switch (activityType) {
    case ACTIVITY_TYPE.WELCOME_ACTIVITY:
      return (
        <div data-testid='welcome-activity'>
          <WelcomeActivitySetUp
            formData={formData}
            handleFormChange={handleFormChange}
          />
        </div>
      );
    case ACTIVITY_TYPE.LOGIN:
      return (
        <div data-testid='login-activity'>
          <LoginActivitySetUp
            formData={formData}
            handleFormChange={handleFormChange}
          />
        </div>
      );
    case ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY:
      return (
        <div data-testid='points-activity'>
          <PointsOnOrderDeliverySetUp
            formData={formData}
            handleFormChange={handleFormChange}
          />
        </div>
      );
    case ACTIVITY_TYPE.POINT_EXPIRATION:
      return (
        <div data-testid='point-expiration-activity'>
          <PointExpirationSetUp
            formData={formData}
            handleFormChange={handleFormChange}
          />
        </div>
      );
    case ACTIVITY_TYPE.PAY_BY_POINTS:
      return null;
    case ACTIVITY_TYPE.CURRENCY_TO_POINTS:
      return null;
    default:
      return null;
  }
};

ActivityTypeComponent.propTypes = {
  activityType: PropTypes.string,
  formData: PropTypes.object,
  handleFormChange: PropTypes.func.isRequired
};

// Helper: Points Allocation Preview
const PointsAllocationPreview = ({
  isCountryCodeFrequency,
  numberOfCases,
  localCurrency,
  localCurrencyCode,
  pointsEarned
}) => (
  <section className='preview-point-allocation float-right'>
    <div className='title'>PREVIEW</div>
    <div>
      <div className='preview-value'>
        {isCountryCodeFrequency ? 'Buy' : 'Spend '}
        &nbsp;
        <span className='font-bold'>
          {isCountryCodeFrequency ? numberOfCases : localCurrency}
        </span>{' '}
        &nbsp;
        {isCountryCodeFrequency ? 'cases' : localCurrencyCode} to earn{' '}
        <span className='font-bold'>{pointsEarned}</span> Points.
      </div>
    </div>
  </section>
);

PointsAllocationPreview.propTypes = {
  isCountryCodeFrequency: PropTypes.bool.isRequired,
  numberOfCases: PropTypes.number,
  localCurrency: PropTypes.number,
  localCurrencyCode: PropTypes.string.isRequired,
  pointsEarned: PropTypes.number
};

// Helper: Welcome Activity Availability
const WelcomeActivityAvailability = ({formData, handleFormChange}) => (
  <>
    <div className='flex items-center'>
      <p className='mb-5 mt-4 flex w-6 text-base font-bold'>Availability</p>
    </div>
    <div className='flex flex-wrap gap-14'>
      <div className='relative z-50 min-w-[288px]'>
        <JsonFormsWrapper
          jsonschema={welcomeActivityAvailabilityJsonSchema}
          uischema={welcomeActivityAvailabilityUiSchema(
            formData?.endDate || '',
            formData?.startDate || '',
            formData
          )}
          data={formData}
          onChange={(data) => handleFormChange(data)}
          formData={formData}
        />
      </div>
      <div className='relative z-30 ml-6 min-w-[200px]'>
        <JsonFormsWrapper
          jsonschema={welcomeActivityHasEndDateSchema}
          uischema={welcomeActivityHasEndUiSchema}
          data={formData}
          onChange={(data) => handleFormChange(data)}
          formData={formData}
        />
      </div>
      {formData.hasEndDate && (
        <div className='relative min-w-[288px]'>
          <JsonFormsWrapper
            jsonschema={welcomeActivityEndDateJsonSchema}
            uischema={welcomeActivityEndDateUiSchema(
              formData?.startDate,
              formData?.endDate
            )}
            data={formData}
            onChange={(data) => handleFormChange(data)}
            formData={formData}
          />
        </div>
      )}
    </div>
  </>
);

WelcomeActivityAvailability.propTypes = {
  formData: PropTypes.object.isRequired,
  handleFormChange: PropTypes.func.isRequired
};

const getSegmentsContainerTestId = (
  isFreeKickGameActivity,
  isVideoActivity,
  isWheelOfFortuneActivity,
  isBackgammonGameActivity
) => {
  if (isFreeKickGameActivity) return 'game-segments-container';
  if (isVideoActivity) return 'video-segments-container';
  if (isWheelOfFortuneActivity) return 'wheel-of-fortune-segments-container';
  if (isBackgammonGameActivity) return 'backgammon-segments-container';
  return 'survey-segments-container';
};

const getSegmentsCardWrapperTestId = (
  isFreeKickGameActivity,
  isVideoActivity,
  isWheelOfFortuneActivity,
  isBackgammonGameActivity
) => {
  if (isFreeKickGameActivity) return 'game-segments-card-wrapper';
  if (isVideoActivity) return 'video-segments-card-wrapper';
  if (isWheelOfFortuneActivity) return 'wheel-of-fortune-segments-card-wrapper';
  if (isBackgammonGameActivity) return 'backgammon-segments-card-wrapper';
  return 'survey-segments-card-wrapper';
};

const ActivitySetupContent = ({
  isSurveyActivity,
  isVideoActivity,
  isFreeKickGameActivity,
  isWheelOfFortuneActivity,
  isBackgammonGameActivity,
  isEasyOrderActivity,
  activityDropdownTypes,
  formData,
  handleResetFormData,
  handleFormChange
}) => {
  const activitySelection = (
    <ActivitySelection
      activityDropdownTypes={activityDropdownTypes}
      formData={formData}
      handleResetFormData={handleResetFormData}
      handleFormChange={handleFormChange}
    />
  );

  if (isSurveyActivity) {
    return (
      <div className='w-full' data-testid='survey-activity'>
        <div className='w-full' data-testid='survey-setup-card-wrapper'>
          <SurveyActivitySetUp
            activitySelection={activitySelection}
            formData={formData}
            handleFormChange={handleFormChange}
          />
        </div>
      </div>
    );
  }

  if (isVideoActivity) {
    return (
      <div className='w-full' data-testid='video-activity'>
        <div className='w-full' data-testid='video-setup-card-wrapper'>
          <VideoActivitySetUp
            activitySelection={activitySelection}
            formData={formData}
            handleFormChange={handleFormChange}
          />
        </div>
      </div>
    );
  }

  if (isFreeKickGameActivity) {
    return (
      <div className='w-full' data-testid='game-activity'>
        <div className='w-full' data-testid='game-setup-card-wrapper'>
          <FreeKickGameSetUp
            activitySelection={activitySelection}
            formData={formData}
            handleFormChange={handleFormChange}
          />
        </div>
      </div>
    );
  }

  if (isWheelOfFortuneActivity) {
    return (
      <div className='w-full' data-testid='wheel-of-fortune-activity'>
        <div
          className='w-full'
          data-testid='wheel-of-fortune-setup-card-wrapper'>
          <WheelOfFortuneSetup
            activitySelection={activitySelection}
            formData={formData}
            handleFormChange={handleFormChange}
          />
        </div>
      </div>
    );
  }

  if (isBackgammonGameActivity) {
    return (
      <div className='w-full' data-testid='backgammon-activity'>
        <div className='w-full' data-testid='backgammon-setup-card-wrapper'>
          <BackgammonGameSetUp
            activitySelection={activitySelection}
            formData={formData}
            handleFormChange={handleFormChange}
          />
        </div>
      </div>
    );
  }

  if (isEasyOrderActivity) {
    return (
      <div className='w-full' data-testid='easy-order-activity'>
        <div className='w-full' data-testid='easy-order-setup-card-wrapper'>
          <EasyOrderActivitySetUp
            activitySelection={activitySelection}
            formData={formData}
            handleFormChange={handleFormChange}
          />
        </div>
      </div>
    );
  }

  return (
    <div className='mb-0 flex flex-col gap-12 lg:flex-row'>
      {activitySelection}
      <ActivityTypeComponent
        activityType={formData?.activityType}
        formData={formData}
        handleFormChange={handleFormChange}
      />
    </div>
  );
};

ActivitySetupContent.propTypes = {
  isSurveyActivity: PropTypes.bool.isRequired,
  isVideoActivity: PropTypes.bool.isRequired,
  isFreeKickGameActivity: PropTypes.bool.isRequired,
  isWheelOfFortuneActivity: PropTypes.bool.isRequired,
  isBackgammonGameActivity: PropTypes.bool.isRequired,
  isEasyOrderActivity: PropTypes.bool.isRequired,
  activityDropdownTypes: PropTypes.object,
  formData: PropTypes.object,
  handleResetFormData: PropTypes.func.isRequired,
  handleFormChange: PropTypes.func.isRequired
};

const ActivitySetUp = ({
  localCurrencyCode,
  showToast,
  toastMessage,
  handleToastClose,
  handleViewActivity
}) => {
  const {
    formData,
    activityDropdownTypes,
    handleFormChange,
    handleResetFormData,
    isLoading,
    showSegments,
    segments,
    segmentsLoading,
    handleAllCustomersChange,
    handleExcludeSegmentsChange,
    handleSegmentsChange,
    handleExcludedSegmentsChange
  } = useActivitySetup();
  const countryCode = useAppSelector((state) => state.countryCode).countryCode;
  const isCountryCodeFrequency = useMemo(
    () => COUNTRY_CODES_FREQUENCY.includes(countryCode),
    [countryCode]
  );
  const {activityType, localCurrency, pointsEarned, numberOfCases} = formData;

  /** SegmentsCard reads `segmentExclude`; merge legacy `excludeSegments` for display parity. */
  const segmentsCardFormData = useMemo(() => {
    const effectiveSegmentExclude =
      Boolean(formData?.segmentExclude) || Boolean(formData?.excludeSegments);
    return {
      ...formData,
      segmentExclude: effectiveSegmentExclude
    };
  }, [formData]);

  const localCurrencyNum = Number(localCurrency) || 0;
  const pointsEarnedNum = Number(pointsEarned) || 0;
  const numberOfCasesNum = Number(numberOfCases) || 0;
  const showPreview = useMemo(
    () => (localCurrencyNum > 0 || numberOfCasesNum > 0) && pointsEarnedNum > 0,
    [localCurrencyNum, numberOfCasesNum, pointsEarnedNum]
  );

  const pointsAllocationUISchema = useMemo(() => {
    if (isCountryCodeFrequency) return pointsAllocationUiSchemaForEG;
    const updatedSchema = JSON.parse(JSON.stringify(pointsAllocationUiSchema));
    updatedSchema.elements[0].elements[0].label = `Local Currency (${localCurrencyCode})*`;
    return updatedSchema;
  }, [isCountryCodeFrequency, localCurrencyCode]);

  const handleJsonFormChange = useCallback(
    (data) => {
      handleFormChange(data);
    },
    [handleFormChange]
  );
  const uiSchema = useMemo(
    () =>
      activityType === ACTIVITY_TYPE.LOGIN
        ? allAvailabilityUiSchema(
            formData?.startDate || '',
            formData?.endDate,
            formData
          )
        : availabilityUiSchema(
            formData?.startDate || '',
            formData?.endDate,
            formData
          ),
    [activityType, formData]
  );

  const surveySegmentsCard = (
    <SegmentsCard
      title='Segments'
      formData={segmentsCardFormData}
      segments={segments}
      segmentsLoading={segmentsLoading}
      handleAllCustomersChange={handleAllCustomersChange}
      handleSegmentExcludeChange={handleExcludeSegmentsChange}
      handleSegmentsChange={handleSegmentsChange}
      handleExcludedSegmentsChange={handleExcludedSegmentsChange}
      hideAddGroup={false}
      showLabelIcon={false}
      infoNotesVariant='activity'
    />
  );

  const isSurveyActivity = activityType === ACTIVITY_TYPE.SURVEY;
  const isVideoActivity = isVideoActivityType(activityType);
  const isFreeKickGameActivity = isFreeKickActivityType(activityType);
  const isWheelOfFortuneActivity = isWheelOfFortuneActivityType(activityType);
  const isBackgammonGameActivity = isBackgammonActivityType(activityType);
  const isEasyOrderActivity = activityType === ACTIVITY_TYPE.EASY_ORDER;
  const isCardLayout =
    isSurveyActivity ||
    isVideoActivity ||
    isFreeKickGameActivity ||
    isWheelOfFortuneActivity ||
    isBackgammonGameActivity ||
    isEasyOrderActivity;

  return (
    <>
      <PageContainer
        data-testid='activity-setup-content'
        {...(isCardLayout
          ? {height: 'auto', minHeight: 'auto'}
          : {minHeight: '64vh'})}>
        {!isCardLayout && (
          <div
            className='flex items-center justify-between'
            data-testid='activity-setup-header'>
            <SectionTitle label={'Activity Setup'} />
          </div>
        )}
        {showToast && (
          <DuplicateAlert
            type='info'
            message={toastMessage}
            show={true}
            onCloseClick={handleToastClose}
            handleClick={handleViewActivity}
            buttonText='View Activity'
          />
        )}
        {isLoading && <TableSkeletonLoader rows={6} spacing={30} />}
        {/* Activity Selection — Survey: activity type full width, then setup/availability cards below */}
        <ActivitySetupContent
          isSurveyActivity={isSurveyActivity}
          isVideoActivity={isVideoActivity}
          isFreeKickGameActivity={isFreeKickGameActivity}
          isWheelOfFortuneActivity={isWheelOfFortuneActivity}
          isBackgammonGameActivity={isBackgammonGameActivity}
          isEasyOrderActivity={isEasyOrderActivity}
          activityDropdownTypes={activityDropdownTypes}
          formData={formData}
          handleResetFormData={handleResetFormData}
          handleFormChange={handleFormChange}
        />
        {/* Ponts on Order Delivery Ponts Allocation */}
        {activityType == ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY && (
          <div className='mb-0' data-testid='activity-point-allocation'>
            <SectionTitle
              label={
                isCountryCodeFrequency
                  ? 'Point Allocation (Number of Cases)'
                  : 'Point Allocation'
              }
            />
            <div className='flex flex-wrap'>
              <JsonFormsWrapper
                jsonschema={
                  isCountryCodeFrequency
                    ? pointsAllocationJsonSchemaForEG
                    : pointsAllocationJsonSchema
                }
                uischema={pointsAllocationUISchema}
                data={formData}
                onChange={handleJsonFormChange}
              />
              {showPreview && (
                <PointsAllocationPreview
                  isCountryCodeFrequency={isCountryCodeFrequency}
                  numberOfCases={numberOfCasesNum}
                  localCurrency={localCurrencyNum}
                  localCurrencyCode={localCurrencyCode}
                  pointsEarned={pointsEarnedNum}
                />
              )}
            </div>
          </div>
        )}
        {/* Pay by Points – Point Allocation */}
        {activityType == ACTIVITY_TYPE.PAY_BY_POINTS && (
          <div className='mb-0' data-testid='pay-by-points-allocation'>
            <SectionTitle label='Point Allocation' />
            <div className='flex flex-wrap items-start gap-4 lg:gap-6'>
              <PayByPointsSetUp
                countryCode={countryCode}
                formData={formData}
                handleFormChange={handleFormChange}
                localCurrencyCode={localCurrencyCode}
              />
            </div>
          </div>
        )}
        {/* Segments (non-Survey/Video/Game) — e.g. Egypt other activity types */}
        {activityType &&
          activityType !== ACTIVITY_TYPE.SURVEY &&
          !isVideoActivityType(activityType) &&
          !isFreeKickActivityType(activityType) &&
          !isWheelOfFortuneActivityType(activityType) &&
          !isBackgammonActivityType(activityType) &&
          activityType !== ACTIVITY_TYPE.EASY_ORDER &&
          showSegments && (
            <SegmentsCard
              formData={segmentsCardFormData}
              segments={segments}
              segmentsLoading={segmentsLoading}
              handleAllCustomersChange={handleAllCustomersChange}
              handleSegmentExcludeChange={handleExcludeSegmentsChange}
              handleSegmentsChange={handleSegmentsChange}
              handleExcludedSegmentsChange={handleExcludedSegmentsChange}
              hideAddGroup
              infoNotesVariant='activity'
            />
          )}

        {/* Welcome Activity Availability*/}
        {activityType == ACTIVITY_TYPE.WELCOME_ACTIVITY && (
          <WelcomeActivityAvailability
            formData={formData}
            handleFormChange={handleFormChange}
          />
        )}
        {/* Pay by Points on Order Delivery – Availability (row layout) */}
        {activityType == ACTIVITY_TYPE.PAY_BY_POINTS && (
          <div className='mb-6' data-testid='pay-by-points-availability'>
            <WelcomeActivityAvailability
              formData={formData}
              handleFormChange={handleFormChange}
            />
          </div>
        )}
        {/* Currency to Points – Point Allocation */}
        {activityType == ACTIVITY_TYPE.CURRENCY_TO_POINTS && (
          <div className='mb-0' data-testid='currency-to-points-allocation'>
            <SectionTitle label='Point Allocation' />
            <div className='flex flex-wrap'>
              <CurrencyToPointsSetUp
                formData={formData}
                handleFormChange={handleFormChange}
              />
            </div>
          </div>
        )}
        {/* Currency to Points – Availability (row layout) */}
        {activityType == ACTIVITY_TYPE.CURRENCY_TO_POINTS && (
          <div className='mb-6' data-testid='currency-to-points-availability'>
            <WelcomeActivityAvailability
              formData={formData}
              handleFormChange={handleFormChange}
            />
          </div>
        )}
        {/* Login Activity & Points on Order Delivery Availability */}
        {(activityType == ACTIVITY_TYPE.LOGIN ||
          activityType == ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY) && (
          <div data-testid='activity-availability' className='mb-8'>
            <p className='mb-5 mt-4 text-base font-bold'>Availability</p>
            <JsonFormsWrapper
              jsonschema={availabilityJsonSchema}
              uischema={uiSchema}
              data={formData}
              onChange={handleJsonFormChange}
            />
          </div>
        )}
      </PageContainer>

      {(activityType === ACTIVITY_TYPE.SURVEY ||
        isVideoActivity ||
        isFreeKickGameActivity ||
        isWheelOfFortuneActivity ||
        isBackgammonGameActivity) &&
        showSegments && (
          <PageContainer
            data-testid={getSegmentsContainerTestId(
              isFreeKickGameActivity,
              isVideoActivity,
              isWheelOfFortuneActivity,
              isBackgammonGameActivity
            )}>
            <div
              data-testid={getSegmentsCardWrapperTestId(
                isFreeKickGameActivity,
                isVideoActivity,
                isWheelOfFortuneActivity,
                isBackgammonGameActivity
              )}>
              {isWheelOfFortuneActivity ? (
                <SegmentsCard
                  title='Segments'
                  formData={segmentsCardFormData}
                  segments={segments}
                  segmentsLoading={segmentsLoading}
                  handleAllCustomersChange={handleAllCustomersChange}
                  handleSegmentExcludeChange={handleExcludeSegmentsChange}
                  handleSegmentsChange={handleSegmentsChange}
                  handleExcludedSegmentsChange={handleExcludedSegmentsChange}
                  hideAddGroup={false}
                  includeChipType={SEGMENT_CHIP_TYPE.OR}
                  showLabelIcon={false}
                  infoNotesVariant='activity'
                />
              ) : (
                surveySegmentsCard
              )}
            </div>
          </PageContainer>
        )}
    </>
  );
};

ActivitySetUp.propTypes = {
  localCurrencyCode: PropTypes.string.isRequired,
  showToast: PropTypes.bool,
  toastMessage: PropTypes.object,
  handleToastClose: PropTypes.func,
  handleViewActivity: PropTypes.func
};

export default React.memo(ActivitySetUp);
